import {
  Box,
  Button,
  DestructiveIcon,
  Divider,
  Flex,
  Input,
  ListItem,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
} from '@modusign/design-system';
import {
  useCreateWorkspacePolicyHistory,
  useGetWorkspacePolicyHistory,
} from '@modusign/openapi-client-user';
import { useState } from 'react';

import { useIsAdminMember } from '@app/features/authorization';
import { useCurrentUserName, useWorkspace } from '@app/hooks/workspace';

const AGREEMENT_TEXT = '이해하고 확인하였습니다';

export default function WorkspaceAdminConfirmModal() {
  const { id: workspaceId, name: workspaceName, type: workspaceType } = useWorkspace();
  const userName = useCurrentUserName();
  const isAdminMember = useIsAdminMember();
  const isAdminMemberInPublicWorkspace = isAdminMember && workspaceType === 'PUBLIC';

  const {
    data,
    isLoading: isWorkspacePolicyHistoryLoading,
    refetch: refetchWorkspacePolicyHistory,
  } = useGetWorkspacePolicyHistory(workspaceId, {
    query: {
      staleTime: 1000,
      refetchOnWindowFocus: false,
      enabled: isAdminMemberInPublicWorkspace,
    },
  });
  const shouldOpenConfirmModal =
    isAdminMemberInPublicWorkspace && !isWorkspacePolicyHistoryLoading && !data?.agreedAt;

  const { mutate: createWorkspacePolicyHistory } = useCreateWorkspacePolicyHistory();
  const [agreementInput, setAgreementInput] = useState('');

  return (
    <Modal
      isOpen={shouldOpenConfirmModal}
      maxWidth="sm"
      onClose={() => {
        setAgreementInput('');
        refetchWorkspacePolicyHistory();
      }}
    >
      <ModalTitle icon={<DestructiveIcon color="iconPrimaryBase" />}>
        공용 워크스페이스의 문서 소유권 안내
      </ModalTitle>
      <ModalContent>
        <Box
          fontWeight="medium"
          mb={7}
          sx={{
            wordBreak: 'keep-all',
          }}
          textAlign="center"
        >
          {userName}님은 {workspaceName}의 최고 관리자로 모든 문서에 대한 소유권을 가집니다.
        </Box>

        <Flex
          as="ul"
          backgroundColor="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          flexDirection="column"
          gap={2}
          lineHeight="20px"
          mb={6}
          px={6}
          py={7}
          textAlign="left"
        >
          <ListItem
            color="textNegativeBase"
            sx={{
              fontWeight: 'medium',
            }}
          >
            멤버가 서명을 요청했거나 요청받은 모든 문서를 열람할 수 있습니다.
          </ListItem>
          <ListItem>워크스페이스의 관리 권한을 가지며, 멤버의 역할을 제어할 수 있습니다.</ListItem>
          <ListItem>문서, 템플릿 등의 소유권을 개인 워크스페이스로 이전할 수 있습니다.</ListItem>
        </Flex>
        <Box
          backgroundColor="boxBackgroundNeutralBase"
          border="1px solid"
          borderColor="boxBorderNeutralBase"
          color="textNeutralLight"
          textAlign="left"
        >
          <Box px={6} py={4}>
            최고 관리자 권한을 모두 확인하셨으면
            <Text color="textNeutralBase"> ‘{AGREEMENT_TEXT}’</Text>를 입력해 주세요.
          </Box>
          <Divider />
          <Flex alignItems="center" color="textNeutralBase" gap={3} p={6}>
            <Box>최고 관리자 권한에 대해</Box>
            <Input
              maxWidth="200px"
              placeholder={AGREEMENT_TEXT}
              value={agreementInput}
              onChange={(e) => setAgreementInput(e.target.value)}
            />
          </Flex>
        </Box>
      </ModalContent>
      <ModalFooter>
        <Button
          disabled={agreementInput !== AGREEMENT_TEXT}
          variant="primary"
          onClick={() => {
            createWorkspacePolicyHistory(
              { workspaceId },
              {
                onSuccess: () => {
                  refetchWorkspacePolicyHistory();
                },
              }
            );
          }}
        >
          확인
        </Button>
      </ModalFooter>
    </Modal>
  );
}
